/**
 * PAINEL DA CARTEIRA DO SIMPLES (aba principal da tela).
 *
 * Espelha o /orquestrador: fundo colorido com cartões de vidro por cima, a
 * linha de etapas ligadas por fio (como o Processos), KPIs com barra e "% do
 * total" (como o Resumo), ranking do que mais trava (como o Erros) e a mesma
 * régua de cor de 0 a 100% (vermelho < 40, âmbar < 75, azul < 100, verde = 100).
 *
 * Os números vêm prontos do backend (GET /simples-nacional/painel). Clicar leva
 * para a aba Empresas já filtrada; clicar numa empresa abre a apuração dela.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CARTAO,
  EYEBROW,
  Faixa,
  Icone,
  TituloSecao,
  VIDRO,
  estiloPilula,
  fundoDaAba,
} from '../../../orquestrador/components/Vidro';
import { C, FONTE_MONO, TONES, Tone } from '../../../orquestrador/paleta';
import { Etapa, IPainel, mensagemErro, snApi } from './api';
import { ETAPAS, prazo } from './PainelKpis';

const ABA = 'resumo' as const; // matiz azul do orquestrador

/** Régua de cor do orquestrador (FAIXA_AMARELO 40, FAIXA_AZUL 75). */
export function tomDoPct(pct: number | null): Tone {
  if (pct === null) return 'idle';
  if (pct >= 100) return 'ok';
  if (pct >= 75) return 'run';
  if (pct >= 40) return 'warn';
  return 'err';
}

const fmtPct = (v: number, casas = 1) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas })}%`;

/** % que nunca mostra 0% ou 100% de mentira (igual textoPct do orquestrador). */
function textoPct(parte: number, total: number): string {
  if (!total) return '—';
  const v = (parte / total) * 100;
  if (parte > 0 && v < 0.1) return '<0,1%';
  if (parte < total && v > 99.9) return '>99,9%';
  return fmtPct(v);
}

const COR_ETAPA = Object.fromEntries(ETAPAS.map(e => [e.filtro, e.cor])) as Record<Etapa, string>;
const ROTULO_ETAPA = Object.fromEntries(ETAPAS.map(e => [e.filtro, e.rotulo])) as Record<
  Etapa,
  string
>;

/** O caminho do mês, na ordem (o erro aparece no cartão "Transmitir"). */
const FLUXO: { etapa: Etapa; titulo: string; acao: string; progresso: number }[] = [
  { etapa: 'nao_calculada', titulo: 'Calcular', acao: 'Calcular / Recalcular', progresso: 0 },
  { etapa: 'bloqueio', titulo: 'Corrigir bloqueio', acao: 'Corrigir a causa', progresso: 20 },
  { etapa: 'atencao', titulo: 'Conferir atenção', acao: 'Aceitar ou corrigir', progresso: 40 },
  { etapa: 'pronta_aprovar', titulo: 'Aprovar', acao: 'Aprovar prontas', progresso: 60 },
  { etapa: 'aprovada', titulo: 'Transmitir', acao: 'Transmitir aprovadas', progresso: 80 },
  { etapa: 'transmitida', titulo: 'DAS e extrato', acao: 'Gerar DAS e extrato', progresso: 90 },
  { etapa: 'concluida', titulo: 'Concluída', acao: 'Nada a fazer', progresso: 100 },
];

/** Nome legível das pendências mais comuns (o resto vira o código sem "_"). */
const PENDENCIAS: Record<string, string> = {
  rbt12_nao_confirmado: 'RBT12 sem fonte confirmada',
  rbt12_acima_limite: 'RBT12 acima do limite do Simples',
  rbt12_sem_historico: 'RBT12 sem histórico',
  rbt12_meses_sem_receita: 'RBT12 com meses sem receita',
  rbt12_proprio_x_receita: 'RBT12 nosso x Receita',
  procuracao_ausente: 'Sem procuração no e-CAC',
  divergencia_dominio: 'Diferente do Domínio',
  serpro_diverge: 'Receita calculou diferente',
  servico_nao_mapeado: 'Serviço sem anexo definido',
  servico_ambiguo: 'Serviço ambíguo',
  variacao_mes_anterior: 'Variação forte contra o mês anterior',
  sequencia_quebrada: 'Quebra de sequência',
  fator_r_sem_folha: 'Fator R sem folha',
  fator_r_limite: 'Fator R no limite de 28%',
  devolucao_abatida: 'Devolução abatida',
  mes_anterior_pendente: 'Mês anterior não transmitido',
  ncm_revisar: 'NCM para revisar',
  sublimite_icms_iss: 'Acima do sublimite (ICMS/ISS fora)',
  retificacao_necessaria: 'Precisa retificar',
  aprovacao_desfeita: 'Aprovação desfeita',
  empresa_sem_cnpj: 'Empresa sem CNPJ',
  municipio_tom_ausente: 'Município sem código TOM',
  cfop_invalido: 'CFOP inválido',
};
export const rotuloPendencia = (c: string) =>
  PENDENCIAS[c] ?? c.replace(/_/g, ' ').replace(/^./, l => l.toUpperCase());

/** Anel de progresso (SVG), como o AnelPercurso do Resumo. */
function Anel({
  pct,
  tamanho = 64,
  grossura = 7,
}: {
  pct: number;
  tamanho?: number;
  grossura?: number;
}) {
  const r = (tamanho - grossura) / 2;
  const volta = 2 * Math.PI * r;
  const t = TONES[tomDoPct(pct)];
  return (
    <svg width={tamanho} height={tamanho} aria-hidden style={{ flex: 'none' }}>
      <circle
        cx={tamanho / 2}
        cy={tamanho / 2}
        r={r}
        fill="none"
        stroke={C.track}
        strokeWidth={grossura}
      />
      <circle
        cx={tamanho / 2}
        cy={tamanho / 2}
        r={r}
        fill="none"
        stroke={t.dot}
        strokeWidth={grossura}
        strokeLinecap="round"
        strokeDasharray={`${(Math.min(pct, 100) / 100) * volta} ${volta}`}
        transform={`rotate(-90 ${tamanho / 2} ${tamanho / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        style={{ fontSize: tamanho / 4.6, fontWeight: 800, fill: C.ink, fontFamily: FONTE_MONO }}
      >
        {pct >= 100 ? '✓' : `${Math.round(pct)}%`}
      </text>
    </svg>
  );
}

/** Barra empilhada das etapas (mesma cor de cada etapa). */
function BarraEtapas({
  etapas,
  total,
  altura = 8,
}: {
  etapas: Record<Etapa, number>;
  total: number;
  altura?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        height: altura,
        borderRadius: 999,
        overflow: 'hidden',
        background: C.track,
      }}
      title={ETAPAS.filter(e => etapas[e.filtro as Etapa])
        .map(e => `${e.rotulo}: ${etapas[e.filtro as Etapa]}`)
        .join(' · ')}
    >
      {total > 0 &&
        ETAPAS.map(e => {
          const v = etapas[e.filtro as Etapa] ?? 0;
          return v ? (
            <div key={e.filtro} style={{ width: `${(v / total) * 100}%`, background: e.cor }} />
          ) : null;
        })}
    </div>
  );
}

const botao: React.CSSProperties = {
  all: 'unset',
  cursor: 'pointer',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
};

export interface IrParaEmpresas {
  situacao?: string;
  responsavel?: string;
}

export function PainelCarteira({
  competencia,
  onIr,
  onAbrirEmpresa,
}: {
  competencia: string;
  onIr: (f: IrParaEmpresas) => void;
  onAbrirEmpresa: (empresaId: number) => void;
}) {
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery<IPainel>({
    queryKey: ['sn-painel', competencia],
    queryFn: () => snApi.painel(competencia),
    refetchInterval: 60000,
  });
  const rotuloMes = `${competencia.slice(5, 7)}/${competencia.slice(0, 4)}`;

  if (error) {
    return (
      <div style={fundoDaAba(ABA)}>
        <Faixa tom="err">Não foi possível carregar o painel: {mensagemErro(error)}</Faixa>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div style={fundoDaAba(ABA)}>
        <div style={{ ...CARTAO, textAlign: 'center', color: C.sub, fontSize: 13 }}>
          Carregando o painel…
        </div>
      </div>
    );
  }

  const { total, etapas } = data;
  const concluidas = etapas.concluida;
  const travadas = etapas.bloqueio + etapas.erro;
  const naoCalc = etapas.nao_calculada;
  const andamento = total - concluidas - travadas - naoCalc;
  const declaradas = etapas.transmitida + etapas.concluida;
  const { data: vence, dias } = prazo(competencia);
  // gargalo: a etapa (antes da conclusão) com mais empresas paradas
  const gargalo = FLUXO.filter(f => f.etapa !== 'concluida').reduce((a, b) =>
    etapas[b.etapa] > etapas[a.etapa] ? b : a,
  );
  let textoPrazo = 'todas declaradas';
  let tomPrazo: Tone = 'ok';
  if (declaradas < total) {
    if (dias > 1) textoPrazo = `vence em ${dias} dias`;
    else if (dias === 1) textoPrazo = 'vence amanhã';
    else if (dias === 0) textoPrazo = 'vence hoje';
    else textoPrazo = `venceu há ${-dias} dia(s)`;
    if (dias < 0) tomPrazo = 'err';
    else tomPrazo = dias <= 5 ? 'warn' : 'run';
  }
  const headline =
    total === 0
      ? 'Nenhuma empresa do Simples na carteira deste mês.'
      : `${total} empresas, ${fmtPct(data.progresso)} do caminho percorrido. ${
          etapas[gargalo.etapa]
            ? `${gargalo.titulo} concentra o maior acúmulo (${etapas[gargalo.etapa]}).`
            : 'Ninguém parado.'
        }`;

  const kpis: { rotulo: string; valor: number; tom: Tone; dica: string; filtro?: string }[] = [
    {
      rotulo: 'Concluídas',
      valor: concluidas,
      tom: 'ok',
      dica: 'Declaração, recibo, DAS e extrato salvos',
      filtro: 'concluida',
    },
    {
      rotulo: 'Travadas',
      valor: travadas,
      tom: 'err',
      dica: `Com bloqueio (${etapas.bloqueio}) ou erro do SERPRO (${etapas.erro}): precisam de correção`,
      filtro: 'bloqueio',
    },
    {
      rotulo: 'Em andamento',
      valor: andamento,
      tom: 'run',
      dica: 'Só atenção, prontas para aprovar, aprovadas ou transmitidas sem DAS',
    },
    {
      rotulo: 'Não calculadas',
      valor: naoCalc,
      tom: 'idle',
      dica: 'Ainda sem cálculo no mês',
      filtro: 'nao_calculada',
    },
  ];

  return (
    <div style={{ ...fundoDaAba(ABA), minWidth: 0, maxWidth: '100%' }}>
      {/* cabeçalho com a frase do mês, como o do orquestrador */}
      <div
        style={{
          ...VIDRO,
          borderRadius: 22,
          padding: '18px 22px',
          display: 'flex',
          gap: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Anel pct={data.progresso} tamanho={78} grossura={8} />
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={EYEBROW}>Competência {rotuloMes} · carteira do Simples</div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 750,
              color: C.ink,
              letterSpacing: '-0.02em',
              marginTop: 2,
            }}
          >
            Painel da carteira
          </div>
          <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>{headline}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span
            style={{
              ...estiloPilula(false, ABA),
              cursor: 'default',
              color: TONES[tomPrazo].fg,
              background: TONES[tomPrazo].bg,
              border: `1px solid ${TONES[tomPrazo].dot}`,
              fontWeight: 700,
            }}
            title="Vencimento do PGDAS-D e do DAS: dia 20 do mês seguinte"
          >
            {declaradas}/{total} declaradas · {textoPrazo} ({vence.toLocaleDateString('pt-BR')})
          </span>
          <span
            style={{ fontSize: 11.5, color: C.sub, display: 'flex', gap: 8, alignItems: 'center' }}
          >
            Dados das{' '}
            {new Date(dataUpdatedAt).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            <button
              type="button"
              style={estiloPilula(false, ABA)}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Atualizando…' : 'Atualizar agora'}
            </button>
          </span>
        </div>
      </div>

      {/* KPIs com barra e % do total (Resumo do orquestrador) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
          gap: 14,
        }}
      >
        {kpis.map(k => {
          const t = TONES[k.tom];
          const pct = total ? (k.valor / total) * 100 : 0;
          const corpo = (
            <div style={{ ...CARTAO, height: '100%' }} title={k.dica}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: t.dot,
                    boxShadow: `0 0 0 4px ${t.bg}`,
                  }}
                />
                <span style={EYEBROW}>{k.rotulo}</span>
              </div>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: 800,
                  color: C.ink,
                  marginTop: 6,
                  fontFamily: FONTE_MONO,
                }}
              >
                {k.valor.toLocaleString('pt-BR')}
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 999,
                  background: C.track,
                  overflow: 'hidden',
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${t.dot}, ${t.fg})`,
                  }}
                />
              </div>
              <div style={{ fontSize: 11.5, color: C.sub, marginTop: 5 }}>
                {textoPct(k.valor, total)} do total
              </div>
            </div>
          );
          return k.filtro ? (
            <button
              key={k.rotulo}
              type="button"
              style={botao}
              onClick={() => onIr({ situacao: k.filtro })}
            >
              {corpo}
            </button>
          ) : (
            <div key={k.rotulo}>{corpo}</div>
          );
        })}
      </div>

      {/* o caminho do mês: cartões ligados por fio (Processos do orquestrador) */}
      <div style={CARTAO}>
        <TituloSecao
          aba={ABA}
          icone={<Icone nome="processos" />}
          titulo="O caminho do mês"
          sub="Cada empresa está em uma etapa. O anel mostra quanto da carteira já passou dela; clique para ver as empresas paradas ali."
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            overflowX: 'auto',
            // a largura dos 7 cartões não pode empurrar a página (só esta faixa rola)
            contain: 'inline-size',
            minWidth: 0,
            gap: 0,
            marginTop: 16,
            paddingBottom: 6,
            scrollSnapType: 'x mandatory',
          }}
        >
          {FLUXO.map((f, i) => {
            const aqui = etapas[f.etapa];
            const passaram = FLUXO.filter(o => o.progresso > f.progresso).reduce(
              (s, o) => s + etapas[o.etapa],
              0,
            );
            // na última etapa o anel mostra quantas chegaram; nas outras, quantas já passaram
            const base = f.etapa === 'concluida' ? aqui : passaram;
            const pctPassou = total ? (base / total) * 100 : 0;
            const erroAqui = f.etapa === 'aprovada' ? etapas.erro : 0;
            let tag = aqui ? `${aqui} empresa(s) aqui` : 'ninguém parado';
            if (f.etapa === 'concluida') tag = `${aqui} concluída(s)`;
            return (
              <React.Fragment key={f.etapa}>
                {i > 0 && (
                  <div
                    aria-hidden
                    style={{
                      flex: 'none',
                      width: 34,
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <div style={{ height: 2, width: '100%', background: C.green }} />
                    <span
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-55%)',
                        fontSize: 12,
                        color: C.greenFg,
                        background: C.greenBg,
                        borderRadius: 6,
                        padding: '0 3px',
                      }}
                    >
                      ›
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  className="orq-elevavel"
                  onClick={() => onIr({ situacao: f.etapa })}
                  style={{
                    ...botao,
                    flex: 'none',
                    width: 'clamp(200px, 15vw, 250px)',
                    scrollSnapAlign: 'start',
                  }}
                  title={`${ROTULO_ETAPA[f.etapa]}: ${aqui} de ${total}. ${textoPct(passaram, total)} da carteira já passou desta etapa.`}
                >
                  <div
                    style={{
                      ...VIDRO,
                      borderRadius: 16,
                      padding: '14px 14px 12px',
                      height: '100%',
                      borderTop: `3px solid ${COR_ETAPA[f.etapa]}`,
                    }}
                  >
                    <div style={{ ...EYEBROW, fontSize: 9.5 }}>Etapa {i + 1}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                      <Anel pct={pctPassou} tamanho={54} grossura={6} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                          {f.titulo}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 800,
                            color: COR_ETAPA[f.etapa],
                            fontFamily: FONTE_MONO,
                            lineHeight: 1.1,
                          }}
                        >
                          {aqui}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        height: 7,
                        borderRadius: 999,
                        background: C.track,
                        overflow: 'hidden',
                        marginTop: 10,
                        display: 'flex',
                      }}
                    >
                      <div
                        style={{
                          width: `${total ? (aqui / total) * 100 : 0}%`,
                          background: COR_ETAPA[f.etapa],
                        }}
                      />
                      {erroAqui > 0 && (
                        <div style={{ width: `${(erroAqui / total) * 100}%`, background: C.red }} />
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: aqui && f.etapa !== 'concluida' ? C.ink : C.sub,
                        marginTop: 7,
                        fontWeight: 600,
                      }}
                    >
                      {tag} · {textoPct(aqui, total)}
                    </div>
                    {erroAqui > 0 && (
                      <div
                        style={{ fontSize: 11.5, color: C.redFg, fontWeight: 700, marginTop: 2 }}
                      >
                        ✕ {erroAqui} com erro do SERPRO
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>↳ {f.acao}</div>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* de 0 a 100% */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: 14,
        }}
      >
        <div style={CARTAO}>
          <TituloSecao
            aba={ABA}
            icone={<Icone nome="analise" />}
            titulo="De 0 a 100%"
            sub="Quantas empresas estão em cada ponto do caminho (0% = não calculada, 100% = concluída)."
          />
          <div
            style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 170, marginTop: 18 }}
          >
            {data.faixas.map(f => {
              const max = Math.max(...data.faixas.map(x => x.empresas), 1);
              const t = TONES[tomDoPct(f.progresso === 0 ? 0 : f.progresso)];
              return (
                <div
                  key={f.progresso}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                  title={`${f.empresas} empresa(s) em ${f.progresso}% · ${textoPct(f.empresas, total)} da carteira`}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 800, color: C.ink, fontFamily: FONTE_MONO }}
                  >
                    {f.empresas}
                  </span>
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 46,
                      height: `${(f.empresas / max) * 120}px`,
                      minHeight: f.empresas ? 4 : 2,
                      borderRadius: '8px 8px 3px 3px',
                      background: f.empresas
                        ? `linear-gradient(180deg, ${t.dot}, ${t.fg})`
                        : C.track,
                    }}
                  />
                  <span style={{ fontSize: 11, color: C.sub, fontFamily: FONTE_MONO }}>
                    {f.progresso}%
                  </span>
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 12,
              fontSize: 11,
              color: C.sub,
            }}
          >
            {(['err', 'warn', 'run', 'ok'] as Tone[]).map((t, i) => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: TONES[t].dot }} />
                {['abaixo de 40%', '40% a 74%', '75% a 99%', '100%'][i]}
              </span>
            ))}
          </div>
        </div>

        {/* evolução dos últimos meses */}
        <div style={CARTAO}>
          <TituloSecao
            aba={ABA}
            icone={<Icone nome="calendario" />}
            titulo="Evolução dos últimos meses"
            sub="Progresso médio da carteira e quantas declararam, mês a mês."
          />
          <div
            style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 170, marginTop: 18 }}
          >
            {data.evolucao.map(m => {
              const t = TONES[tomDoPct(m.total ? m.progresso : null)];
              const atual = m.competencia.slice(0, 7) === competencia.slice(0, 7);
              return (
                <div
                  key={m.competencia}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                  title={`${m.competencia.slice(5, 7)}/${m.competencia.slice(0, 4)}: ${fmtPct(m.progresso)} do caminho · ${m.declaradas} de ${m.total} declaradas · ${m.travadas} travadas`}
                >
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 800,
                      color: C.ink,
                      fontFamily: FONTE_MONO,
                    }}
                  >
                    {fmtPct(m.progresso, 0)}
                  </span>
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 46,
                      height: `${(m.progresso / 100) * 120}px`,
                      minHeight: 2,
                      borderRadius: '8px 8px 3px 3px',
                      background: m.progresso
                        ? `linear-gradient(180deg, ${t.dot}, ${t.fg})`
                        : C.track,
                      outline: atual ? `2px solid ${C.ink}` : undefined,
                      outlineOffset: 2,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: atual ? C.ink : C.sub,
                      fontWeight: atual ? 700 : 400,
                      fontFamily: FONTE_MONO,
                    }}
                  >
                    {m.competencia.slice(5, 7)}/{m.competencia.slice(2, 4)}
                  </span>
                  <span style={{ fontSize: 10.5, color: C.faint }}>
                    {m.declaradas}/{m.total}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* o que mais trava + empresas mais travadas (Erros do orquestrador) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: 14,
        }}
      >
        <div style={CARTAO}>
          <TituloSecao
            aba={ABA}
            icone={<Icone nome="alerta" />}
            titulo="O que mais trava"
            sub="Pendências abertas, contadas por empresa. Bloqueio primeiro (não se aceita: precisa corrigir)."
            direita={<span style={{ ...EYEBROW, color: C.ink }}>{data.travas.length} tipo(s)</span>}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
            {data.travas.length === 0 && (
              <div style={{ fontSize: 12.5, color: C.sub, textAlign: 'center', padding: 18 }}>
                Nenhuma pendência aberta. 🎉
              </div>
            )}
            {data.travas.map(t => {
              const bloq = t.nivel === 'bloqueio';
              const tom = TONES[bloq ? 'err' : 'warn'];
              return (
                <button
                  key={`${t.codigo}-${t.nivel}`}
                  type="button"
                  className="orq-elevavel"
                  style={botao}
                  onClick={() => onIr({ situacao: `pend:${t.codigo}` })}
                  title={`Exemplo: ${t.exemplo}`}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderRadius: 12,
                      background: 'rgba(255,255,255,0.6)',
                      border: '1px solid rgba(255,255,255,0.9)',
                    }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: FONTE_MONO,
                        textTransform: 'uppercase',
                        padding: '2px 7px',
                        borderRadius: 999,
                        color: tom.fg,
                        background: tom.bg,
                      }}
                    >
                      {bloq ? 'bloqueio' : 'atenção'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.ink,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {rotuloPendencia(t.codigo)}
                      </div>
                      <div
                        style={{
                          height: 4,
                          borderRadius: 999,
                          background: C.track,
                          overflow: 'hidden',
                          marginTop: 4,
                        }}
                      >
                        <div
                          style={{ width: `${t.pct * 100}%`, height: '100%', background: tom.dot }}
                        />
                      </div>
                    </div>
                    <span
                      style={{
                        flex: 'none',
                        fontSize: 13,
                        fontWeight: 800,
                        fontFamily: FONTE_MONO,
                        color: C.ink,
                      }}
                    >
                      {t.empresas}
                    </span>
                    <span
                      style={{
                        flex: 'none',
                        width: 48,
                        textAlign: 'right',
                        fontSize: 11,
                        color: C.sub,
                      }}
                    >
                      {textoPct(t.empresas, total)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={CARTAO}>
          <TituloSecao
            aba={ABA}
            icone={<Icone nome="x" />}
            titulo="Empresas mais travadas"
            sub="Com bloqueio ou erro do SERPRO. Clique para abrir a apuração."
            direita={<span style={{ ...EYEBROW, color: C.ink }}>{travadas} no total</span>}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginTop: 14,
              maxHeight: 420,
              overflowY: 'auto',
            }}
          >
            {data.travadas.length === 0 && (
              <div style={{ fontSize: 12.5, color: C.sub, textAlign: 'center', padding: 18 }}>
                Nenhuma empresa travada.
              </div>
            )}
            {data.travadas.map(e => (
              <button
                key={e.empresa_id}
                type="button"
                className="orq-elevavel"
                style={botao}
                onClick={() => onAbrirEmpresa(e.empresa_id)}
              >
                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.6)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    borderLeft: `3px solid ${e.etapa === 'erro' ? C.red : COR_ETAPA.bloqueio}`,
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: C.ink,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {e.nome}
                    </span>
                    <span
                      style={{ fontSize: 11, color: C.sub, fontFamily: FONTE_MONO, flex: 'none' }}
                    >
                      ID {e.external_id} · {e.bloqueios} bloq · {e.pendencias} pend
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: C.sub,
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {e.fiscal ?? 'Sem responsável'} · {e.motivo ?? '—'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* por responsável */}
      <div style={CARTAO}>
        <TituloSecao
          aba={ABA}
          icone={<Icone nome="empresa" />}
          titulo="Por responsável fiscal"
          sub="Quem está mais atrás no caminho aparece primeiro. Clique para ver a carteira dele."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
          {data.responsaveis.map(r => (
            <button
              key={r.responsavel}
              type="button"
              className="orq-elevavel"
              style={botao}
              onClick={() =>
                onIr({ responsavel: r.responsavel === 'Sem responsavel' ? '' : r.responsavel })
              }
              title={`${r.responsavel}: ${r.total} empresa(s), ${fmtPct(r.progresso)} do caminho`}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(0, 1.2fr) 46px minmax(0, 3fr) repeat(3, minmax(0, 70px))',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.6)',
                  border: '1px solid rgba(255,255,255,0.9)',
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.responsavel}
                </span>
                <Anel pct={r.progresso} tamanho={40} grossura={5} />
                <BarraEtapas etapas={r.etapas} total={r.total} />
                <span style={{ fontSize: 12, color: C.sub, textAlign: 'right' }}>
                  <b style={{ color: C.ink, fontFamily: FONTE_MONO }}>{r.total}</b> emp.
                </span>
                <span style={{ fontSize: 12, color: C.greenFg, textAlign: 'right' }}>
                  <b style={{ fontFamily: FONTE_MONO }}>{r.concluidas}</b> concl.
                </span>
                <span
                  style={{ fontSize: 12, color: r.travadas ? C.redFg : C.sub, textAlign: 'right' }}
                >
                  <b style={{ fontFamily: FONTE_MONO }}>{r.travadas}</b> trav.
                </span>
              </div>
            </button>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 12,
            fontSize: 11,
            color: C.sub,
          }}
        >
          {ETAPAS.map(e => (
            <span key={e.filtro} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: e.cor }} />
              {e.rotulo}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
